import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import { Form, FormFields, FormActions, ValidationResult, mapToDropdownOption, FormDropdown } from "sprint-common-web";

import {
    EmbeddedSubtitle,
    EmbeddedSubtitleType
} from "../../../../../../../../common/api/master-api/file-records/details/GetFileRecordDetailsResponse";
import { LanguagesDataContext } from "../../../../../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import {
    ensureVideoFileRecordDetails,
    isVideoWithSubtitle,
    isCleanVideo
} from "../../../../../../../common/file-record-details/FileRecordDetails";
import { useFileRecordDetailsState } from "../../../../../hooks/useFileRecordDetailsState";

export interface EmbeddedSubtitleFormProps {
    isLoading: boolean;
    onCancel?(): void;
    validationResult?: ValidationResult;
    onSubmit(subtitle?: EmbeddedSubtitle): void;
}

interface EmbeddedSubtitleFormInputs {
    subtitle: string;
}

export const EmbeddedSubtitleForm = (props: EmbeddedSubtitleFormProps): JSX.Element => {
    const { fileRecordDetails } = useFileRecordDetailsState();
    const { embeddedSubtitleLanguages } = useContext(LanguagesDataContext);

    ensureVideoFileRecordDetails(fileRecordDetails);

    const getSelectedSubtitle = (id?: string): EmbeddedSubtitle | undefined => {
        if (!id) {
            return undefined;
        }

        const embeddedSubtitle = embeddedSubtitleLanguages.find((s) => s.id === id);

        if (!embeddedSubtitle) {
            return undefined;
        }

        if (embeddedSubtitle.type === EmbeddedSubtitleType.CleanVideo) {
            return { type: EmbeddedSubtitleType.CleanVideo };
        }

        return {
            language: {
                code: embeddedSubtitle.code,
                id: embeddedSubtitle.id,
                name: embeddedSubtitle.name
            },
            type: EmbeddedSubtitleType.Subtitle
        };
    };

    const getDefaultSubtitle = (): string | undefined => {
        if (isCleanVideo(fileRecordDetails.subtitle)) {
            const subtitle = embeddedSubtitleLanguages.find(
                (language) => language.type === EmbeddedSubtitleType.CleanVideo
            );

            return subtitle?.id;
        }

        if (isVideoWithSubtitle(fileRecordDetails.subtitle)) {
            const languageId = fileRecordDetails.subtitle.language.id;

            const subtitle = embeddedSubtitleLanguages.find((language) => language.id === languageId);

            return subtitle?.id;
        }

        return undefined;
    };

    const { handleSubmit, errors, control } = useForm<EmbeddedSubtitleFormInputs>({
        defaultValues: {
            subtitle: getDefaultSubtitle()
        }
    });

    const closeClearedAndCallOnClose = () => {
        if (props.onCancel) {
            props.onCancel();
        }
    };

    const callOnSubmit = (formInputs: EmbeddedSubtitleFormInputs) => {
        const subtitleToSubmit = getSelectedSubtitle(formInputs.subtitle);

        props.onSubmit(subtitleToSubmit);
    };

    return (
        <Form onSubmit={handleSubmit(callOnSubmit)}>
            <FormFields>
                <FormDropdown
                    options={embeddedSubtitleLanguages.map((subtitle) => mapToDropdownOption(subtitle))}
                    label="Subtitle"
                    name="subtitle"
                    control={control}
                    errors={errors}
                />
                <FormActions onCancel={closeClearedAndCallOnClose} isLoading={props.isLoading} />
            </FormFields>
        </Form>
    );
};
